import { coordEq, isTransport, unitAt } from "@dailywars/core/engine";
import type { GameState, Unit } from "@dailywars/core/engine";

import { canCaptureAt } from "#/game/selection/transitions";
import type { SelectionState } from "#/game/selection/types";
import {
  commitCapture,
  commitJoin,
  commitLoad,
  commitPrepareUnload,
  commitWaitOrMove,
  selectTransportUnload,
} from "#/game/store/gameMutations";
import type { StoreState } from "#/game/store/types";

import type { TileSelectContext } from "./tileSelectContext";
import { canShowUnloadCursor } from "./tileSelectSharedHelpers";

type MovePreviewSelection = Extract<SelectionState, { mode: "move-preview" }>;

function tryCommitAllyLoadOrJoin({
  state,
  local,
  sel,
  occupied,
}: {
  state: StoreState;
  local: GameState;
  sel: MovePreviewSelection;
  occupied: Unit | undefined;
}): StoreState | undefined {
  if (!occupied || occupied.owner !== local.currentPlayer) return undefined;
  const load = sel.loadOptions.find((o) => o.targetId === occupied.id);
  if (load) {
    return commitLoad({
      state,
      unitId: sel.unitId,
      transportId: load.targetId,
      path: sel.path,
    });
  }
  const join = sel.joinOptions.find((o) => o.targetId === occupied.id);
  if (!join) return undefined;
  return commitJoin({
    state,
    unitId: sel.unitId,
    targetId: join.targetId,
    path: sel.path,
  });
}

function commitTransportAtDestination({
  state,
  sel,
  unit,
}: {
  state: StoreState;
  sel: MovePreviewSelection;
  unit: Unit;
}): StoreState {
  if (sel.path.length === 0) {
    if (canShowUnloadCursor(unit)) return commitPrepareUnload(state, sel.unitId);
    return commitWaitOrMove({ state, unitId: sel.unitId, path: sel.path });
  }
  const afterMove = commitWaitOrMove({ state, unitId: sel.unitId, path: sel.path });
  const moved = afterMove.localState.units.find((u) => u.id === sel.unitId);
  if (!moved?.cargo) return afterMove;
  if (canShowUnloadCursor(moved)) return commitPrepareUnload(afterMove, sel.unitId);
  if (moved.unloadReady) return selectTransportUnload(afterMove, sel.unitId);
  return afterMove;
}

export function commitMovePreviewDestination({
  ctx,
  sel,
}: {
  ctx: TileSelectContext;
  sel: MovePreviewSelection;
}): StoreState {
  const { state, local } = ctx;
  const unit = local.units.find((u) => u.id === sel.unitId);
  if (!unit) return { ...state, selection: { mode: "none" } };

  const allyResult = tryCommitAllyLoadOrJoin({
    state,
    local,
    sel,
    occupied: unitAt(local, sel.destination),
  });
  if (allyResult) return allyResult;

  if (canCaptureAt({ state: local, unit, pos: sel.destination })) {
    return commitCapture({ state, unitId: sel.unitId, path: sel.path });
  }
  if (isTransport(unit.kind) && unit.cargo) {
    return commitTransportAtDestination({ state, sel, unit });
  }

  if (coordEq(sel.destination, unit.pos)) {
    return commitWaitOrMove({ state, unitId: sel.unitId, path: sel.path });
  }

  return commitWaitOrMove({ state, unitId: sel.unitId, path: sel.path });
}
